import { FC } from 'react';
import { TooltipProps } from 'recharts';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { NET_SALES } from 'features/Dashboard/components/constants';
import { PunchupAreaChart } from 'features/common/components/Punchup/primitives/charts/PunchupAreaChart';
import { PunchupChartTooltip } from 'features/common/components/Punchup/primitives/charts/PunchupChartTooltip';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { TimelineControls } from 'features/PunchupPages/Analytics/TimelineControls';
import { formatCompactCurrency } from 'features/common/utils/chartFormatters';
import { customXTick, toUsFormat } from 'utils/utils';
import { useChart } from 'features/common/hooks/useChart/useChart';

const formatTooltipDate = (label: unknown): string => moment(label as moment.MomentInput).format('MM/DD/YY');
const ticketSalesRowLabel = (): string => 'Ticket sales';

const RevenueTooltip: FC<TooltipProps<number, string>> = (props) => (
	<PunchupChartTooltip
		{...props}
		formatHeader={formatTooltipDate}
		formatValue={toUsFormat}
		formatRowLabel={ticketSalesRowLabel}
	/>
);

const PunchupNetSalesSection: FC = () => {
	const { t } = useTranslation();
	const {
		dataFrame,
		setDisplay,
		displayWindow,
		isLoading,
		minDate,
		maxDate,
		windowStartDate,
		windowEndDate,
		setDateRange,
	} = useChart(NET_SALES);

	const framePayout = toUsFormat(dataFrame?.reduce((acc, d) => acc + d.payout, 0) ?? 0);

	return (
		<PunchupAreaChart
			isLoading={isLoading}
			data={(dataFrame ?? []) as unknown as Record<string, unknown>[]}
			dataKey="payout"
			title={t('analytics-revenue-trend')}
			totalValue={framePayout}
			xAxisFormatter={(v) => customXTick(v as Date)}
			yAxisFormatter={formatCompactCurrency}
			headerRight={
				<TimelineControls
					setDisplay={setDisplay}
					displayWindow={displayWindow}
					minDate={minDate}
					maxDate={maxDate}
					windowStartDate={windowStartDate}
					windowEndDate={windowEndDate}
					setDateRange={setDateRange}
				/>
			}
			customTooltip={RevenueTooltip}
			helpButton={
				<PunchupHelpButton
					mainText="Ticket sales over time, net of refunds. This trend is ticket revenue only — it excludes add-ons and comped tickets, and is not reduced by payouts already sent to you, so its running total is usually lower than the Revenue tile up top."
					secondaryText="For the all-in payout, see the Revenue tile; for a per-tier price estimate, see Ticket Inventory below."
				/>
			}
		/>
	);
};

export default PunchupNetSalesSection;
